import { useEffect, useMemo, useState, useCallback } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { getClassMembers, addClassCourseMember, removeClassCourseMember, getClassCourseMembers } from "@/api/kuizy"

export default function AddLearnersModal({
    courseId,
    courseName,
    classId,
    open = true,
    onClose,
    onSave,
}) {
    if (!open) return null
 
    const [left, setLeft] = useState([])
    const [right, setRight] = useState([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState("")
    const [alert, setAlert] = useState({ open: false, msg: "" })
    const [rows, setRows] = useState([])
    const [total, setTotal] = useState(0)
    const [leftChecked, setLeftChecked] = useState([])
    const [rightChecked, setRightChecked] = useState([])
    const [initialRight, setInitialRight] = useState([])
 
    useEffect(() => {
        if (!open) return

        Promise.all([
            getClassMembers(classId, { all: true }),
            getClassCourseMembers(classId, courseId),
        ]).then(([classRes, courseRes]) => {
            const classLearners = normalize(classRes.data.rows || classRes.data)
            const courseLearners = normalize(courseRes.data || [])

            setRight(courseLearners)
            setInitialRight(courseLearners)
 
            const courseIds = new Set(courseLearners.map(u => u.id))
            setLeft(classLearners.filter(u => !courseIds.has(u.id)))
        })
    }, [open, classId, courseId])

    const normalize = arr =>
        arr.map(u => ({
            ...u,
            id: u.learner_id, // 🔥 จุดสำคัญ
        })) 

    /* ---------- ACTIONS ---------- */
    const moveRight = () => {
        setRight(r => [
            ...r,
            ...left.filter(i => leftChecked.includes(i.id))
        ])
        setLeft(l => l.filter(i => !leftChecked.includes(i.id)))
        setLeftChecked([])
    }
 
    const moveLeft = () => {
        setLeft(l => [...l, ...right.filter(i => rightChecked.includes(i.id))])
        setRight(r => r.filter(i => !rightChecked.includes(i.id)))
        setRightChecked([])
    }
 
    const moveRightAll = () => {
        setRight(r => [...r, ...left])
        setLeft([])
        setLeftChecked([])
    }
 
    const moveLeftAll = () => {
        setLeft(l => [...l, ...right])
        setRight([])
        setRightChecked([])
    }
  
    const handleSave = async () => {
        try {
            setLoading(true)

            const initialIds = new Set(initialRight.map(u => u.learner_id))
            const currentIds = new Set(right.map(u => u.learner_id))

            // ➕ คนที่เพิ่มใหม่
            const toAdd = right.filter(u => !initialIds.has(u.learner_id))

            // ➖ คนที่ถูกลบ
            const toRemove = initialRight.filter(
                u => !currentIds.has(u.learner_id)
            )

            await Promise.all([
                ...toAdd.map(u =>
                    addClassCourseMember({
                        class_id: classId,
                        course_id: courseId,
                        learner_id: u.learner_id,
                    })
                ),
                ...toRemove.map(u =>
                    removeClassCourseMember({
                        class_id: classId,
                        course_id: courseId,
                        learner_id: u.learner_id,
                    })
                ),
            ])

            onSave?.(true)
            onClose?.()
        } catch (err) {
            setAlert({ open: true, msg: "บันทึกไม่สำเร็จ" })
        } finally {
            setLoading(false)
        }
    }


    const loadLearner = useCallback(() => {
        setLoading(true)
        getClassMembers(classId, {
            // searchText: search || undefined,
            all: true,
        })
            .then(res => {
                const d = res?.data
                if (d && typeof d === "object" && ("rows" in d || "total" in d)) {
                    setLeft(d.rows || [])
                    setTotal(d.total || 0)
                } else {
                    const arr = Array.isArray(d) ? d : d?.data ?? []
                    setLeft(arr)
                    setTotal(Array.isArray(arr) ? arr.length : 0)
                }
            })
            .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
            .finally(() => setLoading(false))
    }, [classId])

    useEffect(() => {
        loadLearner()
    }, [loadLearner])
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* BACKDROP */}
            <div
                className="absolute inset-0 bg-black/30"
                onClick={onClose}
            />

            {/* MODAL */}
            <div className="relative bg-white w-[1000px] rounded-xl shadow-lg p-6 z-10">

                {/* HEADER */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">
                        เพิ่มผู้เรียนในหลักสูตร ({courseName})
                    </h2>

                    {/* CLOSE BUTTON */}
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-xl"
                    >
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="grid grid-cols-[400px_100px_400px] gap-6">

                    <ListPanel
                        title="เลือกผู้เรียน"
                        items={left}
                        checked={leftChecked}
                        setChecked={setLeftChecked}
                    />

                    <div className="flex flex-col justify-center gap-3 items-center">
                        <ArrowButton onClick={moveRightAll} disabled={!left.length}>
                            ≫
                        </ArrowButton>
                        <ArrowButton onClick={moveRight} disabled={!leftChecked.length}>
                            &gt;
                        </ArrowButton>
                        <ArrowButton onClick={moveLeft} disabled={!rightChecked.length}>
                            &lt;
                        </ArrowButton>
                        <ArrowButton onClick={moveLeftAll} disabled={!right.length}>
                            ≪
                        </ArrowButton>
                    </div>

                    <ListPanel
                        title="ผู้เรียนที่เลือกแล้ว"
                        items={right}
                        checked={rightChecked}
                        setChecked={setRightChecked}
                    />
                </div>

                {/* FOOTER */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
                    >
                        ยกเลิก
                    </button>
                    <button
                        onClick={handleSave}
                        // disabled={!right.length}
                        className="px-5 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-40"
                    >
                        บันทึก
                    </button>
                </div>
            </div>
        </div>
    )
}

/* =========================
    LIST PANEL
========================= */
function ListPanel({ title, items, checked, setChecked }) {
    const allChecked = items.length > 0 && checked.length === items.length

    const toggleAll = (val) => {
        setChecked(val ? items.map(i => i.id) : [])
    }

    const toggleOne = (item, val) => {
        setChecked(prev =>
            val
                ? [...prev, item.id]
                : prev.filter(id => id !== item.id)
        )
    }

    return (
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col">
            <div className="px-4 py-3 border-b flex items-center gap-3">
                <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={e => toggleAll(e.target.checked)}
                />
                <div>
                    <div className="font-medium">{title}</div>
                    <div className="text-xs text-gray-500">
                        {checked.length}/{items.length} คน
                    </div>
                </div>
            </div>

            <div className="max-h-72 overflow-auto p-2">
                {items.map(item => {
                    const isChecked = checked.includes(item.id)
                    return (
                        <label
                            key={item.id}
                            className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50"
                        >
                            <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={e => toggleOne(item, e.target.checked)}
                            />
                            <span className="text-sm">
                                {item.name} {item.last_name}
                            </span>
                        </label>
                    )
                })}
            </div>
        </div>
    )
}


/* =========================
    BUTTONS
========================= */
function ArrowButton({ children, disabled, onClick }) {
    return (
        <button
            disabled={disabled}
            onClick={onClick}
            className="
        w-10 h-10 rounded-md bg-pink-400
        border border-gray-300
        text-gray-700
        disabled:opacity-40
        hover:bg-pink-600
      "
        >
            {children}
        </button>
    )
}